import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Coins, Plane, MessageSquareWarning } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';
import { useCurrency } from '../../context/CurrencyContext';
import { TravelBookingCard } from '../TravelBookingCard';
import { StatusSelect } from './AdminRows';

export const CurrencyRatesForm = () => {
  const { rates: liveRates } = useCurrency();
  const [rates, setRates] = useState({});
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRates(liveRates); }, [liveRates]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/admin/currencies', { base: 'INR', rates });
      setRates(res.data.rates);
      toast.success('Exchange rates saved — reload to see them site-wide');
    } catch (err) {
      toast.error('Failed to save rates');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-0 shadow-sm max-w-2xl" data-testid="currency-rates-form">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1"><Coins className="w-5 h-5 mr-2 text-teal-600" />Currencies & exchange rates</h3>
        <p className="text-sm text-gray-500 mb-5">All prices are stored in INR. Enter how much 1 INR is worth in each currency. Travelers pick their currency from the header.</p>
        <form onSubmit={save} className="space-y-3">
          {Object.entries(rates).map(([code, rate]) => (
            <div key={code} className="flex items-center gap-3">
              <Label className="w-16 font-mono font-semibold">{code}</Label>
              <Input data-testid={`rate-input-${code}`} type="number" step="0.0001" min="0" value={rate} disabled={code === 'INR'} onChange={(e) => setRates((r) => ({ ...r, [code]: e.target.value }))} className="border-2 max-w-[200px]" />
              {code !== 'INR' && <button type="button" data-testid={`rate-remove-${code}`} onClick={() => setRates((r) => { const c = { ...r }; delete c[code]; return c; })} className="text-sm text-red-600 hover:underline">Remove</button>}
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <Input data-testid="rate-new-code" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase().slice(0, 3))} placeholder="e.g. OMR" className="border-2 w-28 font-mono" />
            <Button type="button" variant="outline" data-testid="rate-add-btn" disabled={newCode.length !== 3 || rates[newCode]} onClick={() => { setRates((r) => ({ ...r, [newCode]: 0.01 })); setNewCode(''); }}>Add currency</Button>
          </div>
          <Button data-testid="rates-save-btn" type="submit" disabled={saving} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white mt-2">{saving ? 'Saving...' : 'Save rates'}</Button>
        </form>
      </CardContent>
    </Card>
  );
};

export const TravelBookingsAdmin = ({ bookings, onUpdated }) => {
  const [integrations, setIntegrations] = useState(null);
  useEffect(() => { api.get('/admin/integrations').then((r) => setIntegrations(r.data)).catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/travel-bookings/${id}/status`, { status });
      onUpdated(res.data);
      toast.success(`Reservation marked ${status}${status !== 'reserved' ? ' — traveler notified' : ''}`);
    } catch (err) {
      toast.error('Failed to update reservation');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-travel-list">
      {integrations && (
        <div className="flex flex-wrap gap-3 text-xs" data-testid="integration-status">
          <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 flex items-center"><Plane className="w-3 h-3 mr-1" />Provider: {integrations.travel_provider} (sandbox fares)</span>
          <span className={`px-3 py-1 rounded-full flex items-center ${integrations.sms === 'configured' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}><MessageSquareWarning className="w-3 h-3 mr-1" />SMS: {integrations.sms === 'configured' ? 'Twilio active' : 'Twilio keys not added'}</span>
          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600">Online payments: not connected</span>
        </div>
      )}
      {bookings.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center text-gray-500">No flight or hotel reservations yet</CardContent></Card>
      ) : bookings.map((b) => (
        <TravelBookingCard key={b.booking_id} b={b} action={
          <div className="mt-2 flex md:justify-end"><StatusSelect value={b.status} options={['reserved', 'confirmed', 'cancelled']} onChange={(s) => setStatus(b.booking_id, s)} testId={`travel-status-select-${b.booking_id}`} /></div>
        } />
      ))}
    </div>
  );
};
